import { AnalyticsBrowser } from "@segment/analytics-next";
import posthog from "posthog-js";
import * as Sentry from "@sentry/react";
import type { TimelinePoint, DataPoint, DateInfo, User } from "./types";
import { POSTHOG_API_KEY, POSTHOG_HOST, SEGMENT_KEY } from "./constants";

type AnalyticsProperties = Record<string, any>;

const sanitizeProperties = <T extends AnalyticsProperties | undefined>(
  properties: T,
): T => {
  if (!properties) return properties;
  const sanitized: AnalyticsProperties = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value !== undefined) sanitized[key] = value;
  }
  return sanitized as T;
};

const segmentAnalytics = SEGMENT_KEY
  ? AnalyticsBrowser.load({ writeKey: SEGMENT_KEY })
  : null;

let postHogInitialized = false;

const initPostHog = () => {
  if (postHogInitialized || !POSTHOG_API_KEY) return;

  posthog.init(POSTHOG_API_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: true,
    capture_pageview: false,
    capture_pageleave: true,
    debug: process.env.NODE_ENV === "development",
    capture_exceptions: {
      capture_console_errors: true,
      capture_unhandled_rejections: true,
      capture_unhandled_errors: true,
    },
  });
  postHogInitialized = true;
};

initPostHog();

export const analytics = {
  enabled: Boolean(POSTHOG_API_KEY || SEGMENT_KEY),

  track: async (eventName: string, properties?: AnalyticsProperties) => {
    const sanitizedProperties = sanitizeProperties(properties);

    if (postHogInitialized) {
      posthog.capture(eventName, sanitizedProperties);
    }

    if (segmentAnalytics) {
      await segmentAnalytics.track(eventName, sanitizedProperties);
    }
  },

  screen: async (name: string, properties?: AnalyticsProperties) => {
    const sanitizedProperties = sanitizeProperties(properties);
    const postHogProperties = {
      ...sanitizedProperties,
      $screen_name: name,
      screen_name: name,
    };

    if (postHogInitialized) {
      posthog.capture("$screen", postHogProperties);
    }

    if (segmentAnalytics) {
      await segmentAnalytics.screen(name, sanitizedProperties);
    }
  },

  identify: async (userId?: string, properties?: AnalyticsProperties) => {
    const sanitizedProperties = sanitizeProperties(properties);

    if (postHogInitialized && userId) {
      posthog.identify(userId, sanitizedProperties);
    }

    if (segmentAnalytics && userId) {
      await segmentAnalytics.identify(userId, sanitizedProperties);
    }
  },

  captureException: async (
    error: unknown,
    properties?: AnalyticsProperties,
  ) => {
    const sanitizedProperties = sanitizeProperties(properties);

    Sentry.captureException(error, { extra: sanitizedProperties });

    if (postHogInitialized) {
      posthog.captureException(error, sanitizedProperties);
    }
  },

  reset: async () => {
    if (postHogInitialized) {
      posthog.reset();
    }

    if (segmentAnalytics) {
      await segmentAnalytics.reset();
    }
  },
};

/**
 * Converts an epoch timestamp (in seconds) to a "Month, Day" format
 */
export const formatEpochToMonthDay = (epochSeconds: number): string => {
  const date = new Date(epochSeconds * 1000); // Convert seconds to milliseconds
  // Get month name
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = months[date.getMonth()];
  // Get day
  const day = date.getDate();
  return `${month} ${day}`;
};

/**
 * Formats an epoch as a zero-padded `MM/DD`.
 *
 * Used for dense daily axes: `formatEpochToMonthDay` produces "Sep 12", which
 * is too wide for a 30-bar band axis in a half-width card — the chart
 * truncates each label to "S...". A fixed-width numeric label fits.
 */
export const formatEpochToNumericMonthDay = (epochSeconds: number): string => {
  const date = new Date(epochSeconds * 1000);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${month}/${day}`;
};

// Add a new function for month-only formatting
export const formatEpochToMonth = (epochSeconds: number): string => {
  const date = new Date(epochSeconds * 1000);
  return date.toLocaleDateString("en-US", {
    month: "short",
  });
};

// Plotting utilities require `DataPoint` type but API returns `TimelinePoint`
export const convertTimelineToDataPoint = (
  points: TimelinePoint[],
): DataPoint[] => {
  return points.map((point) => {
    return {
      day: Math.round((point.start + point.end) / 2),
      count: point.count,
    };
  });
};

export const getCurrentDayTimestamps = (): DateInfo => {
  const now = new Date();
  // Extract day, month, and year
  const day = now.getDate();
  const month = now.getMonth() + 1; // JavaScript months are 0-indexed, so add 1
  const year = now.getFullYear();
  return {
    year: year,
    month: month,
    day: day,
  };
};

export const capitalize = (x: string): string => {
  return x.charAt(0).toUpperCase() + x.slice(1);
};

/**
 * Normalize text
 * @param x (str)
 * @returns (str)
 */
export const standardizeText = (x: string): string => {
  return x
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]/g, "")
    .trim();
};

/**
 * Normalize username to tag
 * @param username
 */
export const usernameToTag = (username: string): string => {
  const tag = standardizeText(username).replace(" ", "-");
  return tag;
};

/**
 * Safe divide and round
 */
export const safeDivideAndRound = (a: number, b: number) => {
  if (b === 0) return 0.0;
  return Math.round((a / b) * 100);
};

export const findUserById = (
  users: User[],
  userId: string,
): User | undefined => {
  return users.find((user) => user.id === userId);
};

const ALPHABET =
  "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

/**
 * Mint a Firestore-style 20-char document id locally without pulling in the
 * Firestore JS SDK. Used for client-side session-id generation on upload.
 */
export function generateSessionId(): string {
  let id = "";
  const arr = crypto.getRandomValues(new Uint8Array(20));
  for (let i = 0; i < 20; i++) id += ALPHABET[arr[i] % ALPHABET.length];
  return id;
}

export const extractApiErrorDetail = (error: unknown): string | undefined => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  return typeof detail === "string" && detail.trim() ? detail : undefined;
};

// Features-not-types plan: the transitional check mirrors the backend alias —
// an org is "api" if the flag is present OR the legacy type says so.
export const hasOrganizationFeature = (
  organization:
    | { features?: string[] | null; organizationType?: string | null }
    | undefined,
  feature: string,
): boolean =>
  Boolean(
    organization &&
      ((organization.features ?? []).includes(feature) ||
        (feature === "api" && organization.organizationType === "api")),
  );
